/**
 * Card renderer (Req 2.1, 2.6): a title plus an ordered list of label/value
 * fields. Missing fields render the title only, without crashing.
 */
import type { CardPayload, CardSection, CardSectionItem } from './types';

/** A normalized display field: both label and value are display strings. */
interface NormalizedField {
  label: string;
  value: string;
}

/**
 * A normalized section item ready to render. Either a plain text line, or a
 * label/value pair reusing the same styling as fields.
 */
type NormalizedItem =
  | { kind: 'text'; text: string }
  | { kind: 'pair'; label: string; value: string };

/** A normalized section: an optional title plus a list of renderable items. */
interface NormalizedSection {
  title?: string;
  items: NormalizedItem[];
}

/**
 * Normalize `payload.fields` into a consistent `{ label, value }[]` list,
 * tolerating the two shapes the model emits in the wild (Req 2.6):
 *
 *  - Array form: `[{ label, value }, ...]` — used as-is, values coerced to
 *    strings, entries missing a usable label are skipped.
 *  - Object form: `{ key: value, ... }` — turned into `{ label: key,
 *    value: String(value) }` via `Object.entries` (which preserves insertion
 *    order for string keys), skipping null/undefined/empty-string values.
 *
 * Anything else (missing / non-object) yields an empty list, so the card
 * renders the title only without crashing.
 */
function normalizeFields(fields: CardPayload['fields']): NormalizedField[] {
  if (Array.isArray(fields)) {
    return fields.reduce<NormalizedField[]>((acc, field) => {
      const label = field?.label;
      if (label == null || label === '') return acc;
      const value = field?.value;
      if (value == null) return acc;
      acc.push({ label: String(label), value: String(value) });
      return acc;
    }, []);
  }

  if (fields && typeof fields === 'object') {
    return Object.entries(fields).reduce<NormalizedField[]>((acc, [key, value]) => {
      if (value == null || value === '') return acc;
      acc.push({ label: key, value: String(value) });
      return acc;
    }, []);
  }

  return [];
}

/**
 * Coerce a scalar section value to a display string, or return null when the
 * value is not usable (null/undefined/empty). Objects/arrays are not valid
 * scalar values here and yield null.
 */
function coerceScalar(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'object') return null;
  const s = String(value);
  return s === '' ? null : s;
}

/**
 * Compact stringify an arbitrary object item that has no clear label/value:
 * join own key:value pairs (scalars only) into a short line, falling back to
 * `JSON.stringify`. Never throws; returns null when nothing usable remains.
 */
function stringifyObjectItem(obj: Record<string, unknown>): string | null {
  const pairs: string[] = [];
  for (const [key, value] of Object.entries(obj)) {
    const scalar = coerceScalar(value);
    if (scalar != null) pairs.push(`${key}: ${scalar}`);
  }
  if (pairs.length > 0) return pairs.join(', ');

  try {
    const json = JSON.stringify(obj);
    return json && json !== '{}' ? json : null;
  } catch {
    return null;
  }
}

/**
 * Normalize a single section item into a renderable shape (Req 2.6, tolerant):
 *
 *  - string → a text line (skipped when empty),
 *  - object with a usable `label` and `value` → a label/value pair,
 *  - any other object → a compact stringified line,
 *  - null/undefined/unusable → null (skipped by the caller).
 */
function normalizeItem(item: CardSectionItem): NormalizedItem | null {
  if (item == null) return null;

  if (typeof item === 'string') {
    return item === '' ? null : { kind: 'text', text: item };
  }

  if (typeof item === 'object') {
    const record = item as Record<string, unknown>;
    const label = coerceScalar(record.label);
    const value = coerceScalar(record.value);
    if (label != null && value != null) {
      return { kind: 'pair', label, value };
    }
    // Object without a clear label/value: compact stringified fallback.
    const text = stringifyObjectItem(record);
    return text == null ? null : { kind: 'text', text };
  }

  // Other scalars (number/boolean) coerced to a text line.
  const scalar = coerceScalar(item);
  return scalar == null ? null : { kind: 'text', text: scalar };
}

/**
 * Normalize a section: guard a non-array `items` (skip), normalize each item,
 * and drop items that yield nothing. Title is passed through untouched.
 */
function normalizeSection(section: CardSection): NormalizedSection {
  const raw = Array.isArray(section?.items) ? section.items : [];
  const items = raw.reduce<NormalizedItem[]>((acc, item) => {
    const normalized = normalizeItem(item);
    if (normalized) acc.push(normalized);
    return acc;
  }, []);
  const title = typeof section?.title === 'string' && section.title !== '' ? section.title : undefined;
  return { title, items };
}

/**
 * Normalize `payload.sections` into a list of renderable sections. Non-array
 * input yields an empty list; sections with no title and no items are dropped
 * so they render nothing extra (Req 2.6).
 */
function normalizeSections(sections: CardPayload['sections']): NormalizedSection[] {
  if (!Array.isArray(sections)) return [];
  return sections.reduce<NormalizedSection[]>((acc, section) => {
    if (section == null || typeof section !== 'object') return acc;
    const normalized = normalizeSection(section);
    if (normalized.title == null && normalized.items.length === 0) return acc;
    acc.push(normalized);
    return acc;
  }, []);
}

export function Card({ title, payload, accent }: { title?: string; payload: CardPayload; accent: string }) {
  const fields = normalizeFields(payload.fields);
  const sections = normalizeSections(payload.sections);

  return (
    <div className="rounded-lg bg-slate-900 p-5">
      {title ? (
        <h2 className="text-xl font-semibold mb-4" style={{ color: accent }}>
          {title}
        </h2>
      ) : null}
      {fields.length > 0 ? (
        <dl className="divide-y divide-slate-800">
          {fields.map((field, i) => (
            <div key={i} className="flex gap-4 py-2">
              <dt className="w-40 shrink-0 text-sm text-slate-400">{field.label}</dt>
              <dd className="text-sm text-slate-100">{field.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {sections.map((section, si) => (
        <div key={si} className="mt-4">
          {section.title ? (
            <h3
              className="text-xs font-semibold uppercase tracking-wide mb-2"
              style={{ color: accent }}
            >
              {section.title}
            </h3>
          ) : null}
          {section.items.length > 0 ? (
            <ul className="space-y-1">
              {section.items.map((item, ii) =>
                item.kind === 'pair' ? (
                  <li key={ii} className="flex gap-4 text-sm">
                    <span className="w-40 shrink-0 text-slate-400">{item.label}</span>
                    <span className="text-slate-100">{item.value}</span>
                  </li>
                ) : (
                  <li key={ii} className="text-sm text-slate-100">
                    {item.text}
                  </li>
                ),
              )}
            </ul>
          ) : null}
        </div>
      ))}
      {fields.length === 0 && sections.length === 0 && !title ? (
        <p className="text-slate-500">No content.</p>
      ) : null}
    </div>
  );
}
